/**
 * Saved additions profiles (PLAN-ADDITIONS-PROFILES).
 *
 * The misc list a brewer adds to most beers (an enzyme, Whirlfloc, Campden),
 * saved under a name and applied to a recipe in one go. The user-facing name
 * is "Additions"; the code name follows `RecipeMisc` and `recipe.addMisc`.
 *
 * The shape is the mash profile's, with two additions. `batchVolumeL` records
 * the batch size the amounts were written for, so applying to a recipe of a
 * different size can scale them. `styleIds` records the style of the recipe it
 * was saved from, which the misc table's menu uses to list it first on recipes
 * of that style.
 *
 * Applying copies the rows into the recipe, the same rule mash and
 * fermentation profiles follow (see `schedule-profile.ts`). There are no
 * built-ins: misc doses are the brewer's own habit. `builtin` is kept, always
 * false, so the shared refusal helpers and the profile menu treat these like
 * the other libraries.
 */

import { z } from 'zod';
import {
  documentIdSchema,
  nameSchema,
  notesSchema,
  positiveSchema,
  timestampSchema,
} from './common';
import { recipeMiscSchema } from './ingredients';

/** Most rows one additions profile holds. Save is offered only at or under it. */
export const MISC_PROFILE_MAX_ROWS = 32;

/** A saved list of misc additions. */
export const miscProfileDocSchema = z.strictObject({
  /** Stable profile id, unique within the library. */
  id: documentIdSchema,
  /** Display name, e.g. `House clean lager`. */
  name: nameSchema,
  /** Always false. Kept so the profile menu and refusals work unchanged. */
  builtin: z.boolean(),
  /** Brewer's prose: what it is for. */
  notes: notesSchema,
  /** Creation time, **ms since epoch**. */
  createdAt: timestampSchema,
  /** Last-modification time, **ms since epoch**. */
  updatedAt: timestampSchema,
  /** Batch volume the amounts were written for, litres. */
  batchVolumeL: positiveSchema.max(10_000),
  /** Styles this suits, by style id. Ordering hint only. */
  styleIds: z.array(z.string().min(1).max(80)).max(20),
  /** The additions, as the recipe stored them. */
  miscs: z.array(recipeMiscSchema).max(MISC_PROFILE_MAX_ROWS),
});

/** @see miscProfileDocSchema */
export type MiscProfileDoc = z.infer<typeof miscProfileDocSchema>;

/**
 * Fields an update may change.
 *
 * Like `mashProfileChangesSchema`, nothing here may carry a `.default()`: the
 * schema is `.partial()`-ed, and a default would put the key into every patch
 * that left it out.
 */
export const miscProfileChangesSchema = miscProfileDocSchema
  .omit({ id: true, builtin: true, createdAt: true, updatedAt: true })
  .partial();
